import { Alert, Badge, Button, Card, Center, Group, Loader, SimpleGrid, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { errorMessage } from '../api/client';
import { contractsApi, studentsApi } from '../api/studio';
import type { SaveStudentPayload, StudentResponse } from '../api/types';
import { PageTitle } from '../components/PageTitle';
import { ru } from '../i18n/ru';
import { formatAge, formatDate, formatMoney } from '../studio/format';
import { StudentFormModal } from '../studio/StudentFormModal';

export function MyStudentsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<StudentResponse | null>(null);
  const [formOpen, setFormOpen] = useState(false);

  const students = useQuery({ queryKey: ['my-students'], queryFn: studentsApi.my });

  const save = useMutation({
    mutationFn: (payload: SaveStudentPayload) =>
      editing === null
        ? studentsApi.create(payload)
        : studentsApi.update(editing.id, payload),
    onSuccess: async () => {
      notifications.show({ color: 'green', message: ru.profile.saved });
      await queryClient.invalidateQueries({ queryKey: ['my-students'] });
    },
  });

  const contract = useMutation({
    mutationFn: ({ id, fullName }: { id: string; fullName: string }) =>
      contractsApi.download(id, fullName),
    onError: (error) => {
      notifications.show({ color: 'red', message: errorMessage(error) });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (student: StudentResponse) => {
    setEditing(student);
    setFormOpen(true);
  };

  return (
    <Stack gap="xl">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <PageTitle title={ru.students.myTitle} />
        <Button onClick={openCreate}>{ru.students.add}</Button>
      </Group>

      {students.isPending && (
        <Center h={120}>
          <Loader />
        </Center>
      )}

      {students.isError && (
        <Alert color="red" title={ru.common.error}>
          {errorMessage(students.error, ru.common.loadFailed)}
        </Alert>
      )}

      {students.isSuccess && students.data.length === 0 && (
        <Alert color="gray" variant="light" maw={560}>
          {ru.students.emptyMine}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {(students.data ?? []).map((student) => (
          <Card key={student.id} padding="lg">
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text fw={600} c="graphite.9">
                  {student.fullName}
                </Text>
                {!student.isActive && (
                  <Badge color="gray" variant="light">
                    {ru.students.inactive}
                  </Badge>
                )}
              </Group>

              <Stack gap={4}>
                <Field label={ru.students.group} value={student.groupName} />
                <Field
                  label={ru.students.birthDate}
                  value={`${formatDate(student.birthDate)} · ${formatAge(student.age)}`}
                />
                <Field label={ru.students.price} value={formatMoney(student.monthlyPrice)} />
              </Stack>

              {/* Стоимость приходит с сервера и здесь только читается —
                  менять её может лишь администратор. */}
              <Text size="xs" c="graphite.5">
                {ru.students.priceReadOnly}
              </Text>

              <Group gap="xs">
                <Button variant="default" size="xs" onClick={() => openEdit(student)}>
                  {ru.students.edit}
                </Button>

                {/* Договор печатается и подписывается на бумаге: электронной
                    подписи в проекте нет. */}
                <Button
                  variant="default"
                  size="xs"
                  loading={contract.isPending && contract.variables?.id === student.id}
                  onClick={() =>
                    contract.mutate({ id: student.id, fullName: student.fullName })
                  }
                >
                  {ru.students.contract}
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>

      {/* key заставляет форму пересоздаться: значения по умолчанию
          react-hook-form читает только при первом рендере. */}
      {formOpen && (
        <StudentFormModal
          key={editing?.id ?? 'new'}
          opened
          student={editing}
          onClose={() => setFormOpen(false)}
          onSubmit={async (payload) => {
            await save.mutateAsync(payload);
          }}
        />
      )}
    </Stack>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="graphite.5" style={{ minWidth: 130 }}>
        {label}
      </Text>
      <Text size="sm" c="graphite.9">
        {value}
      </Text>
    </Group>
  );
}
